import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { filingApi } from '../../api/endpoints';
import { useAuthStore } from '../../stores/auth.store';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import type { Filing } from '@motra/shared';

// Simple compliance calendar items
const COMPLIANCE_ITEMS = [
  { month: 1, label: 'GST/VAT Filing Q4' },
  { month: 4, label: 'GST/VAT Filing Q1' },
  { month: 7, label: 'GST/VAT Filing Q2' },
  { month: 10, label: 'GST/VAT Filing Q3' },
  { month: 3, label: 'Annual Tax Return (Corporate)' },
  { month: 4, label: 'ACRA Annual Return' },
];

export default function FilingCompliance() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const queryClient = useQueryClient();
  const currentMonth = new Date().getMonth() + 1;

  const { data: res, isLoading } = useQuery({
    queryKey: ['filing', account?.id],
    queryFn: () => filingApi.get(account!.id),
    enabled: !!account?.id,
  });

  const filings = res?.data ?? [];

  const updateFiling = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Filing> }) =>
      filingApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['filing'] }),
  });

  const [editFiling, setEditFiling] = useState<Filing | null>(null);
  const [form, setForm] = useState<Partial<Filing>>({});

  function openEdit(filing: Filing) {
    setEditFiling(filing);
    setForm({ ...filing });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!editFiling) return;
    try {
      await updateFiling.mutateAsync({ id: editFiling.id, data: form });
      setEditFiling(null);
    } catch { /* empty */ }
  }

  const columns = [
    { header: 'Title', accessor: 'title' as keyof Filing },
    { header: t('category', 'Category'), accessor: 'category' as keyof Filing },
    { header: t('description', 'Description'), accessor: 'description' as keyof Filing },
    {
      header: 'Tags',
      accessor: 'tags' as keyof Filing,
      render: (val: unknown) => String(val ?? ''),
    },
    {
      header: t('date', 'Created'),
      accessor: 'created_at' as keyof Filing,
      render: (val: unknown) =>
        val ? new Date(String(val)).toLocaleDateString() : '—',
    },
    {
      header: 'Actions',
      accessor: 'id' as keyof Filing,
      render: (_v: unknown, row: Filing) => (
        <button
          className="btn btn--sm btn--outline"
          onClick={(e) => { e.stopPropagation(); openEdit(row); }}
        >
          {t('edit', 'Edit')}
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('filing', 'Filing & Compliance')}</h1>
      </div>

      {/* Compliance calendar */}
      <div className="compliance-calendar form-card">
        <h3 className="form-section-title">Compliance Calendar</h3>
        <div className="compliance-list">
          {COMPLIANCE_ITEMS.sort((a, b) => a.month - b.month).map((item, i) => {
            const monthName = new Date(2024, item.month - 1).toLocaleString('default', { month: 'long' });
            const isUpcoming = item.month >= currentMonth && item.month <= currentMonth + 2;
            return (
              <div key={i} className={`compliance-item${isUpcoming ? ' compliance-item--upcoming' : ''}`}>
                <span className="compliance-item__month">{monthName}</span>
                <span className="compliance-item__label">{item.label}</span>
                {isUpcoming && <span className="badge badge--warning">Upcoming</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Filing documents table */}
      <div className="form-card">
        <h3 className="form-section-title">Documents</h3>
        <DataTable
          columns={columns}
          data={filings}
          loading={isLoading}
          rowKey={(r) => r.id}
          emptyMessage="No filing documents"
        />
      </div>

      {/* Edit modal */}
      <Modal
        open={!!editFiling}
        onClose={() => setEditFiling(null)}
        title="Edit Filing"
      >
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              type="text"
              className="form-input"
              value={form.title ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('category', 'Category')}</label>
            <input
              type="text"
              className="form-input"
              value={form.category ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('description', 'Description')}</label>
            <textarea
              className="form-textarea"
              value={form.description ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Tags (comma-separated)</label>
            <input
              type="text"
              className="form-input"
              value={form.tags ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn--outline" onClick={() => setEditFiling(null)}>
              {t('cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn--primary" disabled={updateFiling.isPending}>
              {t('save', 'Save')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
